import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Download, ExternalLink, Share2, Calendar, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import StaircaseViz from "../components/StaircaseViz";
import { useLang } from "../lib/LangContext";
import { api } from "../lib/api";

export default function ResultsPage() {
  const { t, lang } = useLang();
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [state, setState] = useState({ loading: true, analysis: null, lead: null, ref: null, answers: null, error: null });
  const [config, setConfig] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const cfg = await api.publicConfig();
        setConfig(cfg);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) Try session storage cache
        const cached = sessionStorage.getItem("ariovis.lastResult");
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.analysis) {
            // Re-fetch to ensure lang matches
            if (parsed.analysis.lang === lang) {
              if (!cancelled) setState({ loading: false, analysis: parsed.analysis, lead: parsed.lead, ref: parsed.ref, answers: parsed.answers, error: null });
              return;
            }
          }
        }
        // 2) Load by token if provided
        if (token) {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume/${token}?lang=${lang}`);
          if (!r.ok) throw new Error("resume failed");
          const d = await r.json();
          if (!cancelled) setState({ loading: false, analysis: d.analysis, lead: d.lead, ref: d.ref, answers: d.answers, error: null });
        } else {
          if (!cancelled) setState({ loading: false, analysis: null, lead: null, ref: null, answers: null, error: "no_token" });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, analysis: null, lead: null, ref: null, answers: null, error: "load_failed" });
      }
    })();
    return () => { cancelled = true; };
  }, [token, lang]);

  const analysis = state.analysis;

  const downloadPdf = async () => {
    if (!analysis || !state.answers || !state.lead) return;
    try {
      const lead = { ...state.lead, consent: true, email: state.lead.email || "matthieu.filizzola@ariovis.fr" };
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/report/pdf?lang=${lang}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers: state.answers, lead, ref: state.ref }),
      });
      if (!r.ok) throw new Error("pdf failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ariovis-roadmap-iam-${state.ref || "rapport"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("wizard.generic_error"));
    }
  };

  const shareLink = async () => {
    if (!token) return;
    const url = `${window.location.origin}/resultats?token=${token}&lang=${lang}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("results.share_copied"));
    } catch (_) { /* ignore */ }
  };

  if (state.loading) {
    return (
      <div>
        <Header variant="results" />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="animate-spin text-slate-500" size={32} />
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div>
        <Header variant="results" />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="text-slate-500">{t("wizard.generic_error")}</div>
          <Link to="/diagnostic" className="btn-primary mt-6 inline-flex">{t("hero.cta_primary")}</Link>
        </div>
      </div>
    );
  }

  const cur = analysis.zt_level.current;
  const tgt = analysis.target.target;
  const sw = analysis.strongest_weakest;

  return (
    <div>
      <SEO page="results" canonicalPath="/resultats" />
      <Header variant="results" />

      {/* Hero */}
      <section className="border-b border-slate-200 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 motif-circuit opacity-[0.06]" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative">
          <div className="overline" style={{ color: "#DE0F72" }}>{t("results.overline")}</div>
          <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight" data-testid="results-title">
            {t("results.title")}
          </h1>
          <p className="mt-3 text-slate-300 max-w-2xl">{t("results.subtitle")}</p>

          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={downloadPdf} className="btn-primary" style={{ background: "#DE0F72", borderColor: "#DE0F72" }} data-testid="results-download-pdf">
              <Download size={16} /> {t("results.download_pdf")}
            </button>
            <button onClick={shareLink} className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:border-white text-white bg-transparent px-6 py-3 font-semibold text-sm transition-colors" data-testid="results-share-link">
              <Share2 size={16} /> {t("results.share")}
            </button>
            {config.calendly_url && (
              <a href={config.calendly_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:border-white text-white bg-transparent px-6 py-3 font-semibold text-sm transition-colors" data-testid="results-book-meeting">
                <Calendar size={16} /> {lang === "en" ? "Book a meeting" : "Prendre rendez-vous"}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Executive summary */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.summary")}</h2>
        <p className="mt-4 text-slate-600 leading-relaxed max-w-3xl" data-testid="results-summary-text">{analysis.summary_text}</p>

        <div className="mt-8 grid md:grid-cols-4 gap-4">
          <SummaryCard label={t("results.current_level")} value={`${lang === "en" ? "Level" : "Niveau"} ${cur}`} accent="#DE0F72" testid="summary-current" />
          <SummaryCard label={t("results.target_level")} value={`${lang === "en" ? "Level" : "Niveau"} ${tgt}`} accent="#8A0E9E" testid="summary-target" />
          <SummaryCard label={t("results.global_score")} value={`${analysis.global_score?.toFixed(1) || "0.0"} / 5`} testid="summary-score" />
          <SummaryCard label={t("results.strongest_axis")} value={analysis.axis_labels?.[sw.strongest] || "—"} testid="summary-strongest" small />
        </div>

        <div className="mt-4 text-sm text-slate-500 italic max-w-3xl" data-testid="target-rationale">
          {analysis.target?.rationale}
        </div>
      </section>

      {/* Staircase */}
      <section className="max-w-7xl mx-auto px-6 pb-14">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.staircase")}</h2>
        <div className="mt-8 card-ariovis">
          <StaircaseViz current={cur} target={tgt} />
        </div>
      </section>

      {/* Radar */}
      <section className="max-w-7xl mx-auto px-6 pb-14">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.axis_radar")}</h2>
        <div className="mt-8 card-ariovis" data-testid="results-radar">
          <MaturityRadar axisScores={analysis.axis_scores} axisLabels={analysis.axis_labels} />
        </div>
      </section>

      {/* Forces */}
      {analysis.strengths?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-14">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.strengths")}</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {analysis.strengths.map((s, i) => (
              <div key={i} className="card-ariovis" data-testid={`strength-${i}`}>
                <div className="text-sm font-bold text-slate-900">{s.title}</div>
                <div className="mt-2 text-sm text-slate-600">{s.text}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Gaps */}
      <section className="max-w-7xl mx-auto px-6 pb-14">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.gaps")}</h2>
        <div className="mt-8 space-y-4">
          {analysis.gaps?.map((g, i) => (
            <div key={i} className="card-ariovis" data-testid={`gap-${i}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="text-base font-bold text-slate-900">{g.title}</div>
                <span className="text-xs font-bold px-2 py-1 rounded-full border border-slate-200 text-slate-600">{g.urgency}</span>
              </div>
              <div className="mt-3 grid md:grid-cols-2 gap-3 text-sm text-slate-600">
                <div><span className="font-bold text-slate-900">{t("results.constat")}.</span> {g.constat}</div>
                <div><span className="font-bold text-slate-900">{t("results.consequence")}.</span> {g.consequence}</div>
                <div><span className="font-bold text-slate-900">{t("results.capacity")}.</span> {g.capacity}</div>
                <div><span className="font-bold text-slate-900">{t("results.dependency")}.</span> {g.dependency}</div>
                <div className="md:col-span-2"><span className="font-bold text-slate-900">{t("results.action_reco")}.</span> {g.action}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section className="max-w-7xl mx-auto px-6 pb-14">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.roadmap")}</h2>
        <div className="mt-8 space-y-3" data-testid="results-roadmap">
          {analysis.roadmap?.map((s) => <RoadmapItem key={s.index} step={s} />)}
        </div>
      </section>

      {/* 5-year plan */}
      {analysis.five_year_plan?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-14">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.five_year_plan")}</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-3xl">{t("results.five_year_intro")}</p>
          <div className="mt-8 grid md:grid-cols-5 gap-3" data-testid="results-five-year">
            {analysis.five_year_plan.map((y, i) => {
              const color = ["#9E0E40", "#FF9900", "#B58900", "#FF76B7", "#8A0E9E"][i];
              return (
                <div key={i} className="card-ariovis p-4" data-testid={`year-${i + 1}`}>
                  <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color }}>{y.year}</div>
                  <div className="mt-2 text-sm font-bold text-slate-900 leading-tight">{y.focus}</div>
                  <div className="mt-3 text-[11px] text-slate-500">{t("results.linked_step")}</div>
                  <div className="text-xs text-slate-700 font-semibold leading-tight">{y.step_ref_title}</div>
                  {y.kpi && (
                    <>
                      <div className="mt-3 text-[11px] text-slate-500">{t("results.year_kpi")}</div>
                      <div className="text-xs text-slate-700 leading-relaxed">{y.kpi}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Scoping breakdown */}
      {analysis.pricing?.substeps?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-14">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.scoping_title")}</h2>
          <div className="mt-6 card-ariovis" data-testid="results-scoping">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 text-xs font-bold uppercase tracking-widest text-slate-500">{t("results.scoping_col_task")}</th>
                  <th className="text-right py-2 text-xs font-bold uppercase tracking-widest text-slate-500 w-20">{t("results.scoping_col_days")}</th>
                  <th className="text-right py-2 text-xs font-bold uppercase tracking-widest text-slate-500 w-28">{t("results.scoping_col_price")}</th>
                </tr>
              </thead>
              <tbody>
                {analysis.pricing.substeps.map((s, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`scoping-row-${s.key}`}>
                    <td className="py-2.5 text-slate-700">{s.name}</td>
                    <td className="py-2.5 text-right text-slate-900 font-semibold tabular-nums">{s.days.toFixed(1)}</td>
                    <td className="py-2.5 text-right text-slate-900 tabular-nums">{Math.round(s.days * analysis.pricing.tjm_eur).toLocaleString(lang === "en" ? "en-US" : "fr-FR")} €</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold">
                  <td className="py-3 text-slate-900">{t("results.scoping_total")}</td>
                  <td className="py-3 text-right text-slate-900 tabular-nums">{analysis.pricing.days.toFixed(1)}</td>
                  <td className="py-3 text-right text-slate-900 tabular-nums" data-testid="scoping-total">{analysis.pricing.price_eur.toLocaleString(lang === "en" ? "en-US" : "fr-FR")} €</td>
                </tr>
              </tbody>
            </table>
            <p className="mt-4 text-xs text-slate-500 italic">{t("results.approx_note")}</p>
          </div>
        </section>
      )}

      {/* Priority matrix */}
      <section className="max-w-7xl mx-auto px-6 pb-14">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.priority_matrix")}</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <MatrixCell title={t("results.quick_wins")} items={analysis.priority_matrix?.quick_wins} accent="#FF9900" />
          <MatrixCell title={t("results.structuring")} items={analysis.priority_matrix?.structuring} accent="#DE0F72" />
          <MatrixCell title={t("results.foundations")} items={analysis.priority_matrix?.foundations} accent="#9E0E40" />
          <MatrixCell title={t("results.future")} items={analysis.priority_matrix?.future} accent="#8A0E9E" />
        </div>
      </section>

      {/* CTAs */}
      {analysis.ctas?.length > 0 && (
        <section className="border-t border-slate-200 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6 py-14">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.ctas")}</h2>
            <div className="mt-8 space-y-4">
              {analysis.ctas.map((c) => {
                const url = c.url === "IAG_STUDY_URL" ? config.iag_study_url : (config.calendly_url || config.contact_url);
                return (
                  <div key={c.id} className="card-ariovis" data-testid={`cta-${c.id}`}>
                    <div className="text-base font-bold text-slate-900">{c.title}</div>
                    <div className="mt-2 text-sm text-slate-600 max-w-3xl">{c.text}</div>
                    <a href={url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 btn-primary" data-testid={`cta-${c.id}-link`}>
                      {c.label} <ExternalLink size={14} />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Offers */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("results.offers")}</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {analysis.offers?.map((o) => (
            <div key={o.id} className={`card-ariovis relative ${o.recommended ? "ring-2 ring-offset-2 ring-[#DE0F72]" : ""}`} data-testid={`offer-${o.id}`}>
              {o.recommended && (
                <span className="absolute -top-3 left-6 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full text-white" style={{ background: "#DE0F72" }}>
                  {t("results.recommended")}
                </span>
              )}
              <div className="text-lg font-bold text-slate-900">{o.name}</div>
              <div className="mt-2 text-sm text-slate-600">{o.when}</div>
              <div className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">{t("results.offer_contents")}</div>
              <ul className="mt-2 space-y-1 text-sm text-slate-600 list-disc list-inside">
                {o.contents?.slice(0, 5).map((c, i) => <li key={i}>{c}</li>)}
                {o.contents?.length > 5 && <li className="italic">…</li>}
              </ul>
              <div className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">{t("results.offer_price")}</div>
              <div className="mt-1 text-sm text-slate-900 font-semibold">{o.price}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-14">
        <p className="text-xs text-slate-500 italic border-l-2 border-slate-300 pl-3" data-testid="results-disclaimer">
          {t("results.disclaimer")}
        </p>
      </section>

      <Footer />
    </div>
  );
}

function SummaryCard({ label, value, accent, testid, small }) {
  return (
    <div className="card-ariovis relative overflow-hidden" data-testid={testid}>
      {accent && <div className="absolute top-0 left-0 right-0 h-1" style={{ background: accent }} />}
      <div className="overline">{label}</div>
      <div className={`mt-3 font-bold text-slate-900 ${small ? "text-lg" : "text-2xl md:text-3xl"}`}>{value}</div>
    </div>
  );
}

function MatrixCell({ title, items, accent }) {
  return (
    <div className="card-ariovis">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <span className="w-2 h-2 rounded-full" style={{ background: accent }} />
        {title}
      </div>
      <ul className="mt-3 space-y-1 text-sm text-slate-600">
        {items && items.length > 0 ? items.map((i, idx) => <li key={idx}>• {i.title}</li>) : <li className="italic text-slate-400">—</li>}
      </ul>
    </div>
  );
}

function RoadmapItem({ step }) {
  const { t } = useLang();
  const [open, setOpen] = useState(step.index <= 2);
  const color = ["#9E0E40", "#FF9900", "#B58900", "#FF76B7", "#8A0E9E"][step.index - 1];
  return (
    <div className="card-ariovis" data-testid={`roadmap-step-${step.index}`}>
      <button type="button" className="w-full flex items-start justify-between gap-4" onClick={() => setOpen(!open)}>
        <div className="text-left">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full text-white font-bold text-sm flex items-center justify-center" style={{ background: color }}>
              {step.index}
            </span>
            <div>
              <div className="text-base font-bold text-slate-900">{step.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">{step.phase} · {step.horizon} · {t("results.target_level_short")} {step.target_level}</div>
            </div>
          </div>
        </div>
        <ChevronDown className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} size={20} />
      </button>
      {open && (
        <div className="mt-4 pl-12 text-sm text-slate-600 space-y-3">
          <div><span className="font-bold text-slate-900">{t("results.phase")}.</span> {step.phase}</div>
          <div><span className="font-bold text-slate-900">{t("results.objective")}.</span> {step.objective}</div>
          <div>
            <div className="font-bold text-slate-900">{t("results.actions_key")}</div>
            <ul className="mt-1 space-y-1 list-disc list-inside">
              {step.actions?.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
          <KV label={t("results.prerequisites")} value={(step.prerequisites || []).join(", ") || "—"} />
          <KV label={t("results.dependencies")} value={(step.dependencies || []).join(", ") || "—"} />
          <KV label={t("results.deliverables")} value={(step.deliverables || []).join(", ") || "—"} />
          <KV label={t("results.risks")} value={(step.risks || []).join(", ") || "—"} />
        </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return <div><span className="font-bold text-slate-900">{label}.</span> {value}</div>;
}

function MaturityRadar({ axisScores, axisLabels }) {
  const data = Object.keys(axisLabels || {}).map((k) => ({
    axis: axisLabels[k],
    score: axisScores[k] === null ? 0 : axisScores[k] || 0,
    __evaluated: axisScores[k] !== null,
  }));
  return (
    <div className="w-full h-[400px]">
      <ResponsiveContainer>
        <RadarChart data={data} outerRadius="70%">
          <PolarGrid stroke="#e2e8f0" />
          <PolarAngleAxis dataKey="axis" tick={{ fill: "#334155", fontSize: 11 }} />
          <PolarRadiusAxis domain={[0, 5]} tick={{ fill: "#94a3b8", fontSize: 10 }} angle={30} />
          <Radar name="score" dataKey="score" stroke="#DE0F72" fill="#DE0F72" fillOpacity={0.22} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}
